import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import { Phone, Mail } from 'lucide-react';
import { LogoFooter } from './Logo';

const Footer = () => {
  const { t } = useLanguage();
  const { isDark } = useTheme();

  return (
    <footer
      data-testid="footer"
      className={`relative py-16 md:py-24 border-t transition-colors duration-500 ${
        isDark 
          ? 'bg-[#0A0A0A] border-[#D4AF37]/10' 
          : 'bg-[#FAF7F0] border-[#D4AF37]/20'
      }`}
    >
      {/* Grain texture */}
      <div 
        className="absolute inset-0 opacity-[0.02] pointer-events-none"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
        }}
      />

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <div className="flex flex-col items-center text-center">
          {/* Logo */}
          <a 
            href="/"
            data-testid="footer-logo"
            className="mb-8 transition-transform duration-300 hover:scale-[1.02]"
          >
            <LogoFooter isDark={isDark} />
          </a>

          {/* Baseline */}
          <p 
            data-testid="footer-baseline"
            className={`max-w-md mb-10 text-sm md:text-base ${
              isDark ? 'text-[#F8F5EE]/60' : 'text-[#1A1A1A]/60'
            }`}
          >
            {t('footer.baseline')}
          </p>

          {/* Contact Info */}
          <div className="flex flex-col sm:flex-row items-center gap-6 mb-10">
            <a
              href="tel:+33619362520"
              data-testid="footer-phone"
              className={`flex items-center gap-2 text-sm transition-colors duration-300 ${
                isDark ? 'text-white/70 hover:text-[#D4AF37]' : 'text-[#1A1A1A]/70 hover:text-[#D4AF37]'
              }`}
            >
              <Phone className="w-4 h-4 text-[#D4AF37]" />
              <span>+33 6 19 36 25 20</span>
            </a>
            <span className={`hidden sm:block ${isDark ? 'text-[#D4AF37]/30' : 'text-[#D4AF37]/50'}`}>|</span>
            <a
              href="mailto:contact@hbtransfert.com"
              data-testid="footer-email"
              className={`flex items-center gap-2 text-sm transition-colors duration-300 ${
                isDark ? 'text-white/70 hover:text-[#D4AF37]' : 'text-[#1A1A1A]/70 hover:text-[#D4AF37]'
              }`}
            >
              <Mail className="w-4 h-4 text-[#D4AF37]" />
              <span>contact@hbtransfert.com</span>
            </a>
          </div>

          {/* Separator */}
          <div className="w-full max-w-xs flex items-center gap-4 mb-10">
            <div className={`flex-1 h-px bg-gradient-to-r from-transparent ${isDark ? 'to-[#D4AF37]/30' : 'to-[#D4AF37]/40'}`} />
            <div className="w-2 h-2 rotate-45 bg-[#D4AF37]/50" />
            <div className={`flex-1 h-px bg-gradient-to-l from-transparent ${isDark ? 'to-[#D4AF37]/30' : 'to-[#D4AF37]/40'}`} />
          </div>

          {/* Legal Links */}
          <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-8 mb-8">
            <Link
              to="/mentions-legales"
              data-testid="footer-legal-link"
              className={`text-xs uppercase tracking-wider transition-colors duration-300 ${
                isDark ? 'text-white/50 hover:text-[#D4AF37]' : 'text-[#1A1A1A]/50 hover:text-[#D4AF37]'
              }`}
            >
              {t('footer.legal')}
            </Link>
            <Link
              to="/politique-confidentialite"
              data-testid="footer-privacy-link"
              className={`text-xs uppercase tracking-wider transition-colors duration-300 ${
                isDark ? 'text-white/50 hover:text-[#D4AF37]' : 'text-[#1A1A1A]/50 hover:text-[#D4AF37]'
              }`}
            >
              {t('footer.privacy')}
            </Link>
          </div>

          {/* Copyright */}
          <p 
            data-testid="footer-copyright"
            className={`text-xs ${isDark ? 'text-white/30' : 'text-[#1A1A1A]/30'}`}
          >
            {t('footer.copyright')}
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;